import React, { useState } from "react";
import "./CookieConsent.scss";
import { PageRoutes } from "../../utils/pageRoutes";

interface DrawerProps {
  onClose: () => void;
  onSave: () => void;
}

const categories = [
  {
    id: "necessary",
    label: "Necessary cookies",
    mandatory: true,
    desc: "These cookies are essential for you to browse the website and use its features.",
  },
  {
    id: "session",
    label: "Session cookies",
    desc: "These cookies are temporary and expire once you close your browser (or once your session ends).",
  },
  {
    id: "persistent",
    label: "Persistent cookies",
    desc: "This category encompasses all cookies that remain on your device until you erase them or your browser does, depending on the cookie’s expiration date.",
  },
  {
    id: "performance",
    label: "Performance cookies",
    desc: "Performance cookies are used to see how you are using the website.",
  },
  {
    id: "functional",
    label: "Functional cookies",
    desc: "They allow websites to remember login data, region settings, language preferences, and other essential information.",
  },
  {
    id: "marketing",
    label: "Marketing cookies",
    desc: "Used to deliver ads more relevant to you and your interests.",
  },
  {
    id: "unclassified",
    label: "Unclassified cookies",
    desc: "Cookies that are being classified.",
  },
];

const CookieConsentDrawer: React.FC<DrawerProps> = ({ onClose, onSave }) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("consent");
  const [prefs, setPrefs] = useState<Record<string, boolean>>({
    necessary: true,
    session: false,
    persistent: false,
    performance: false,
    functional: false,
    marketing: false,
    unclassified: false,
  });

  const toggleExpand = (id: string) => {
    setExpanded(expanded === id ? null : id);
  };

  const toggleSwitch = (id: string) => {
    if (id === "necessary") return; // cannot disable
    setPrefs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify({ version: "v1", prefs })
    );
    onSave();
  };

  return (
    <div className="cookie-drawer">
      {/* Cookie 🍪 Image */}
      <button className="close-btn" onClick={onClose}>
        ×
      </button>

      {/* Header */}
     <div className="cookie-header">
  <div className="tabs">
    <button
      className={activeTab === "consent" ? "active" : ""}
      onClick={() => setActiveTab("consent")}
    >
      Consent
    </button>
    <button
      className={activeTab === "details" ? "active" : ""}
      onClick={() => setActiveTab("details")}
    >
      Details
    </button>
    <button
      className={activeTab === "about" ? "active" : ""}
      onClick={() => setActiveTab("about")}
    >
      About
    </button>
  </div>
</div>


      {/* Tab Content */}
      <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between',height: '90%'}}>
      <div className="tab-content">
        {activeTab === "consent" && (
          <div>
            <h3>Your privacy matters to us</h3>
            <p>
              Welcome to TICCA! <b>This website uses cookies</b> to enhance your
              browsing experience and provide you with essential functionality.
              We also use cookies that help us analyze and understand how you
              use this website. These cookies will be stored in your browser
              only with your consent. We value your privacy, and it’s important
              for you to understand how these cookies work.{" "}
              <a href={PageRoutes.CookiePolicy}>Cookie Policy</a>
            </p>

            <div className="cookie-list">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`cookie-category ${
                    expanded === cat.id ? "expanded" : ""
                  }`}
                >
                  <div
                    className="category-header"
                    onClick={() => toggleExpand(cat.id)}
                  >
                    <span className="cat-label">
                      {cat.label}{" "}
                      {cat.mandatory && (
                        <span className="mandatory">Always accept</span>
                      )}
                    </span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={prefs[cat.id]}
                        disabled={cat.mandatory}
                        onChange={() => toggleSwitch(cat.id)}
                      />
                      <span className="slider"></span>
                    </label>
                    <span className="arrow">⌄</span>
                  </div>
                  <div className="category-body">
                    <p>{cat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "details" && (
          <div>
            <h3>Your privacy matters to us</h3>
            <p>
              Welcome to TICCA! This website uses cookies to enhance your
              browsing experience and provide you with essential functionality.
              We also use cookies that help us analyze and understand how you
              use this website. These cookies will be stored in your browser
              only with your consent. We value your privacy, and it’s important
              for you to understand how these cookies work.{" "}
              <a href={PageRoutes.CookiePolicy}>Cookie Policy</a>
            </p>
            {/* same categories, expanded with descriptions */}
            <div className="cookie-list expanded-view">
              {categories.map((cat) => (
                <div key={cat.id} className="cookie-category expanded">
                  <div className="category-header">
                    <span className="cat-label">
                      {cat.label}{" "}
                      {cat.mandatory && (
                        <span className="mandatory">Always accept</span>
                      )}
                    </span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={prefs[cat.id]}
                        disabled={cat.mandatory}
                        onChange={() => toggleSwitch(cat.id)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <div className="category-body">
                    <p>{cat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "about" && (
          <div>
            <h3>Your privacy matters to us</h3>
            <p>
              A cookie is data that a website stores on a user's computer. It
              retains specific information from a web browser session on that
              particular website, which can be accessed later.
              <br />
              Strictly necessary cookies are integral to our operation, that’s
              why you will be unable to control the cookie. Rather, the Reset
              Cookie Settings Option enables you to manage or control your
              cookie preferences regarding sharing.
              <br />
              Before providing consent, please take a moment to review our{" "}
              <a href={PageRoutes.PrivacyPolicy}>Privacy Policy</a>. It outlines
              the process and purpose of data collection from the TICCA website,
              software, or mobile application.{" "}
              <a href={PageRoutes.CookiePolicy}>Cookie Policy</a>
            </p>
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="cookie-footer">
        <button className="accept-btn" onClick={handleSave}>
          Accept All
        </button>
        <button className="necessary-btn" onClick={handleSave}>
          Necessary Only
        </button>
        <button className="customize-btn" onClick={handleSave}>
          Customize
        </button>
      </div>
      </div>
    </div>
  );
};

export default CookieConsentDrawer;
